import React, { useState, useEffect, useCallback } from "react";

/* ================= CONFIG ================= */

const PRODUCT_IMAGE_BASE =
  process.env.REACT_APP_ASSETS_BASE_URL ||
  "https://auth.afrogebeya.com/uploads/products";

const getProductImageUrl = (image) =>
  image?.storedName
    ? `${PRODUCT_IMAGE_BASE}/${image.storedName.replace(/\\/g, "/")}`
    : "https://via.placeholder.com/64x64?text=No+Image";

/* ================= MAIN COMPONENT ================= */

export default function ProductsTable({ products = [], onApprove, onReject }) {
  const [expanded, setExpanded] = useState(null);
  const [galleryImages, setGalleryImages] = useState([]);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const [showGallery, setShowGallery] = useState(false);
  const [touchStartX, setTouchStartX] = useState(null);

  const toggleExpanded = (productId) =>
    setExpanded(expanded === productId ? null : productId);

  const openGallery = (images, index = 0) => {
    setGalleryImages(images.map(getProductImageUrl));
    setGalleryIndex(index);
    setShowGallery(true);
  };

  const closeGallery = () => {
    setShowGallery(false);
    setGalleryImages([]);
  };

  const prevImage = useCallback(() => {
    setGalleryIndex((i) => (i === 0 ? galleryImages.length - 1 : i - 1));
  }, [galleryImages]);

  const nextImage = useCallback(() => {
    setGalleryIndex((i) => (i === galleryImages.length - 1 ? 0 : i + 1));
  }, [galleryImages]);

  /* Keyboard navigation */
  useEffect(() => {
    const handleKey = (e) => {
      if (!showGallery) return;
      if (e.key === "Escape") closeGallery();
      if (e.key === "ArrowLeft") prevImage();
      if (e.key === "ArrowRight") nextImage();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [showGallery, prevImage, nextImage]);

  /* Touch support */
  const handleTouchStart = (e) => setTouchStartX(e.touches[0].clientX);
  const handleTouchEnd = (e) => {
    if (touchStartX === null) return;
    const diff = e.changedTouches[0].clientX - touchStartX;
    if (diff > 50) prevImage();
    if (diff < -50) nextImage();
    setTouchStartX(null);
  };

  return (
    <div className="card-body p-0">
      <div className="table-responsive">
        <table className="table table-hover align-middle mb-0">
          <thead className="table-light">
            <tr>
              <th style={{ width: 40 }} />
              <th>Product</th>
              <th>Category</th>
              <th>Seller</th>
              <th>Price</th>
              <th>Moderation</th>
              <th>Visibility</th>
              <th>Created</th>
              <th style={{ minWidth: 220 }}>Actions</th>
            </tr>
          </thead>

          <tbody>
            {products.map((p) => (
              <React.Fragment key={p.productId}>
                <tr>
                  <td>
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => toggleExpanded(p.productId)}
                    >
                      {expanded === p.productId ? "−" : "+"}
                    </button>
                  </td>

                  {/* Product */}
                  <td>
                    <div className="d-flex align-items-center">
                      <img
                        src={getProductImageUrl(p.mainImage)}
                        alt={p.productName}
                        className="rounded me-2"
                        style={{
                          width: 48,
                          height: 48,
                          objectFit: "cover",
                          cursor: "pointer",
                        }}
                        onClick={() =>
                          openGallery([p.mainImage, ...(p.images || [])], 0)
                        }
                      />
                      <div>
                        <div className="fw-semibold">{p.productName}</div>
                        <small className="text-muted">
                          SKU: {p.sku} • {p.brand || "—"}
                        </small>
                      </div>
                    </div>
                  </td>

                  {/* Category */}
                  <td>
                    <div>{p.category?.name}</div>
                    <small className="text-muted">{p.subCategory?.name}</small>
                  </td>

                  {/* Seller */}
                  <td>
                    <small className="text-muted">{p.sellerId}</small>
                  </td>

                  {/* Price */}
                  <td>
                    <div className="fw-semibold">
                      {typeof p.price === "number"
                        ? `${p.price.toLocaleString()} ${p.currency}`
                        : "—"}
                    </div>
                    {p.discountPrice > 0 && (
                      <small className="text-success">
                        Discount: {p.discountPrice}
                      </small>
                    )}
                  </td>

                  {/* Moderation */}
                  <td>
                    <ModerationBadge status={p.moderationStatus} />
                  </td>

                  {/* Visibility */}
                  <td>
                    <span
                      className={`badge bg-${
                        p.isVisible ? "success" : "secondary"
                      }`}
                    >
                      {p.isVisible ? "Visible" : "Hidden"}
                    </span>
                  </td>

                  {/* Created */}
                  <td>
                    <small>{new Date(p.createdAt).toLocaleDateString()}</small>
                  </td>

                  {/* Actions */}
                  <td>
                    <button
                      className="btn btn-sm btn-success me-2"
                      disabled={p.moderationStatus === "APPROVED"}
                      onClick={() => onApprove(p.productId)}
                    >
                      Approve
                    </button>
                    <RejectInline productId={p.productId} onReject={onReject} />
                  </td>
                </tr>

                {expanded === p.productId && (
                  <tr className="bg-light">
                    <td colSpan={9}>
                      <ProductDetails product={p} openGallery={openGallery} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>

        {/* ================= IMAGE GALLERY ================= */}
        {showGallery && (
          <div
            onClick={closeGallery}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            style={{
              position: "fixed",
              inset: 0,
              backgroundColor: "rgba(0,0,0,0.9)",
              zIndex: 1050,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {/* Close */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                closeGallery();
              }}
              style={galleryButtonStyle("right")}
            >
              ×
            </button>

            {/* Prev */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                prevImage();
              }}
              style={galleryButtonStyle("left")}
            >
              ‹
            </button>

            {/* Image */}
            <div style={{ position: "relative" }}>
              <img
                src={galleryImages[galleryIndex]}
                alt="Gallery"
                style={{
                  maxWidth: "90vw",
                  maxHeight: "90vh",
                  objectFit: "contain",
                }}
                onClick={(e) => e.stopPropagation()}
              />
              <div style={counterStyle}>
                {galleryIndex + 1} / {galleryImages.length}
              </div>
            </div>

            {/* Next */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                nextImage();
              }}
              style={galleryButtonStyle("right", true)}
            >
              ›
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ================= HELPERS ================= */

function ModerationBadge({ status }) {
  if (!status) {
    return <span className="badge bg-secondary">Unknown</span>;
  }

  const map = {
    PENDING_REVIEW: "warning",
    APPROVED: "success",
    REJECTED: "danger",
  };

  return (
    <span className={`badge bg-${map[status] || "secondary"}`}>
      {status.replaceAll("_", " ")}
    </span>
  );
}

function RejectInline({ productId, onReject }) {
  const [reason, setReason] = useState("");

  const submit = () => {
    onReject(productId, reason);
    setReason("");
  };

  return (
    <div className="d-inline-flex align-items-center">
      <input
        className="form-control form-control-sm me-2"
        placeholder="Reject reason"
        style={{ width: 160 }}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
      />
      <button
        className="btn btn-sm btn-danger"
        disabled={!reason.trim()}
        onClick={submit}
      >
        Reject
      </button>
    </div>
  );
}

function ProductDetails({ product, openGallery }) {
  return (
    <div className="p-3">
      <div className="row g-4">
        <div className="col-md-6">
          <Section title="Description">
            <p className="text-muted mb-0">{product.description || "—"}</p>
          </Section>

          <Section title="Attributes">
            {Object.keys(product.attributes || {}).length === 0 ? (
              <span className="text-muted">No attributes</span>
            ) : (
              <ul className="small mb-0">
                {Object.entries(product.attributes).map(([k, v]) => (
                  <li key={k}>
                    <strong>{k}:</strong> {v}
                  </li>
                ))}
              </ul>
            )}
          </Section>

          <Section title="Tags">
            {product.tags.length === 0 ? (
              <span className="text-muted">No tags</span>
            ) : (
              product.tags.map((t, i) => (
                <span key={i} className="badge bg-secondary me-1">
                  {t}
                </span>
              ))
            )}
          </Section>

          {product.images?.length > 0 && (
            <Section title="Gallery">
              <div className="d-flex flex-wrap">
                {product.images.map((img, i) => (
                  <img
                    key={i}
                    src={getProductImageUrl(img)}
                    alt={`Gallery ${i + 1}`}
                    className="rounded me-2 mb-2"
                    style={{
                      width: 64,
                      height: 64,
                      objectFit: "cover",
                      cursor: "pointer",
                    }}
                    onClick={() =>
                      openGallery([product.mainImage, ...product.images], i + 1)
                    }
                  />
                ))}
              </div>
            </Section>
          )}
        </div>

        <div className="col-md-6">
          <Section title="Shipping & Dimensions">
            <ul className="small mb-0">
              <li>
                Weight: {product.weight} {product.weightUnit}
              </li>
              <li>
                Dimensions: {product.width} × {product.height} ×{" "}
                {product.length} {product.dimensionUnit}
              </li>
              <li>Shipping Class: {product.shippingClass}</li>
            </ul>
          </Section>

          <Section title="Analytics">
            <ul className="small mb-0">
              <li>Rating: {product.averageRating}</li>
              <li>Total Ratings: {product.totalRatings}</li>
              <li>Total Sold: {product.totalSold}</li>
              <li>Total Views: {product.totalViews}</li>
            </ul>
          </Section>

          <Section title="Moderation Audit">
            <ul className="small text-muted mb-0">
              <li>Status: {product.moderationStatus}</li>
              <li>Reason: {product.moderationReason || "—"}</li>
              <li>Moderated By: {product.moderatedBy || "—"}</li>
              <li>
                At:{" "}
                {product.moderatedAt
                  ? new Date(product.moderatedAt).toLocaleString()
                  : "—"}
              </li>
            </ul>
          </Section>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="mb-3">
      <h6 className="mb-1">{title}</h6>
      {children}
    </div>
  );
}

/* ================= STYLES ================= */

const galleryButtonStyle = (side, isNext = false) => ({
  position: "absolute",
  top: side === "right" && !isNext ? 20 : "50%",
  right: side === "right" ? 20 : undefined,
  left: side === "left" ? 20 : undefined,
  transform: side === "right" && !isNext ? undefined : "translateY(-50%)",
  fontSize: 32,
  color: "#fff",
  background: "none",
  border: "none",
  cursor: "pointer",
});

const counterStyle = {
  position: "absolute",
  top: 10,
  right: 10,
  backgroundColor: "rgba(0,0,0,0.6)",
  color: "#fff",
  padding: "4px 8px",
  borderRadius: 12,
  fontSize: 14,
  fontWeight: "bold",
};
